import { planDownscale } from "../domain/resize";
import type { SourceImageMeta } from "../domain/types";
import { putAsset } from "./assets";

export class ImageLoadError extends Error {}

async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file);
  } catch {
    // createImageBitmap が対応しない形式でも <img> なら復号できる場合がある
    // （ブラウザのネイティブ対応形式に依存。例: 一部環境の HEIC）
    const url = URL.createObjectURL(file);
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () =>
          reject(
            new ImageLoadError(
              "この画像形式は読み込めませんでした。JPEG / PNG / WebP の画像か、スクリーンショットの撮り直しをお試しください。",
            ),
          );
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

export interface LoadedImage {
  meta: SourceImageMeta;
}

// 取り込み → （必要なら）端末内縮小 → アセット登録。
// 画像データはこの関数の外（ネットワーク）へ出ない
export async function loadImageFile(file: Blob): Promise<LoadedImage> {
  const source = await decode(file);
  const width = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
  const height = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
  if (width === 0 || height === 0) {
    throw new ImageLoadError("画像を読み込めませんでした。別の画像をお試しください。");
  }

  const plan = planDownscale(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ImageLoadError("画像の処理に失敗しました。");
  ctx.drawImage(source, 0, 0, plan.width, plan.height);
  if (source instanceof ImageBitmap) source.close();

  const assetId = putAsset(canvas);
  return {
    meta: {
      assetId,
      width: plan.width,
      height: plan.height,
      wasDownscaled: plan.downscaled,
    },
  };
}
